import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import LocationSearch from '@/components/LocationSearch';
import { 
  Sparkles, Calendar, Clock, Phone, Mail, User, MapPin, 
  CheckCircle, Star, BookOpen, Flame, Heart, Home,
  ArrowRight, MessageCircle
} from 'lucide-react';
import { useAppConfig } from '@/hooks/useAppConfig';

const API_URL = '';

const PriestServices = () => {
  const appConfig = useAppConfig();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    state: '',
    serviceType: '',
    preferredDate: '',
    preferredTime: '',
    additionalDetails: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const services = [
    {
      id: 'griha_pravesh',
      title: 'Griha Pravesh',
      description: 'House warming ceremony for your new home',
      icon: Home,
      popular: true
    },
    {
      id: 'satyanarayan_puja',
      title: 'Satyanarayan Puja',
      description: 'Worship for prosperity and well-being',
      icon: Star,
      popular: true
    },
    {
      id: 'vastu_shanti',
      title: 'Vastu Shanti',
      description: 'Remedy for Vastu doshas in your property',
      icon: Sparkles,
      popular: false
    },
    {
      id: 'navagraha_puja',
      title: 'Navagraha Puja',
      description: 'Planetary peace and blessings',
      icon: Flame,
      popular: false
    },
    {
      id: 'ganesh_puja',
      title: 'Ganesh Puja',
      description: 'Remove obstacles, bring prosperity',
      icon: Heart,
      popular: true
    },
    {
      id: 'other',
      title: 'Other Ceremonies',
      description: 'Custom religious ceremonies',
      icon: BookOpen,
      popular: false
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceSelect = (serviceId) => {
    setFormData(prev => ({ ...prev, serviceType: serviceId }));
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.serviceType) {
      newErrors.serviceType = 'Please select a service';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/priest-services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: 'website',
          created_at: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      setSubmitted(true);
      toast.success('Your request has been submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-32" data-testid="priest-services-success">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Request Submitted!</h1>
          <p className="text-gray-600 mb-8">
            Thank you for your request. Our team will contact you within 24 hours to discuss your requirements and connect you with a verified priest in your area.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-amber-800 mb-2">What happens next?</h3>
            <ul className="text-left text-amber-700 space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Our team reviews your request and finds suitable priests in your area</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>We'll call you to confirm details and provide priest profiles</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>You choose the priest and finalize the ceremony date</span>
              </li>
            </ul>
          </div>
          <Button 
            onClick={() => window.location.href = '/'}
            className="bg-primary"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-32" data-testid="priest-services-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Verified Priests Across Pan India
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="priest-services-title">
            Book a Priest for Your Ceremony
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Connect with experienced and verified priests for all your religious ceremonies. 
            From Griha Pravesh to Vastu Shanti, we have you covered.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Service Selection */}
          <div>
            <h2 className="text-xl font-semibold mb-6">Select a Service</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((service) => {
                const Icon = service.icon;
                const isSelected = formData.serviceType === service.id;
                return (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => handleServiceSelect(service.id)}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 hover:border-primary/50 bg-white'
                    }`}
                    data-testid={`service-${service.id}`}
                  >
                    {service.popular && (
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                        Popular
                      </span>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                      isSelected ? 'bg-primary text-white' : 'bg-amber-100 text-amber-600'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-semibold text-gray-900">{service.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                    {isSelected && (
                      <CheckCircle className="absolute top-4 right-4 w-5 h-5 text-primary" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Benefits */}
            <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Why Book Through ZenStudios?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Verified Priests</p>
                    <p className="text-sm text-gray-500">All priests are background-verified and experienced</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Transparent Pricing</p>
                    <p className="text-sm text-gray-500">No hidden charges, clear pricing upfront</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Pan India Coverage</p>
                    <p className="text-sm text-gray-500">Available in all major cities across India</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
            <h2 className="text-xl font-semibold mb-6">Your Details</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-gray-700">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="pl-10"
                    required
                    data-testid="priest-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="pl-10"
                    required
                    data-testid="priest-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700">Email (Optional)</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="pl-10"
                    data-testid="priest-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">
                    City <span className="text-red-500">*</span>
                  </Label>
                  <LocationSearch
                    value={formData.city}
                    onChange={(val) => {
                      handleLocationChange('city', val);
                      if (errors.city) setErrors({...errors, city: ''});
                    }}
                    placeholder="Search city..."
                    types="city,town"
                    state={formData.state}
                    required
                    className={`mt-1 ${errors.city ? '[&_input]:border-red-500' : ''}`}
                    data-testid="priest-city-input"
                    onLocationSelect={(location) => {
                      if (location?.state) handleLocationChange('state', location.state);
                    }}
                  />
                  {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                </div>
                <div>
                  <Label className="text-gray-700">
                    State <span className="text-red-500">*</span>
                  </Label>
                  <LocationSearch
                    value={formData.state}
                    onChange={(val) => {
                      handleLocationChange('state', val);
                      if (errors.state) setErrors({...errors, state: ''});
                    }}
                    placeholder="Search state..."
                    types="state"
                    required
                    className={`mt-1 ${errors.state ? '[&_input]:border-red-500' : ''}`}
                    data-testid="priest-state-input"
                  />
                  {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="preferredDate" className="text-gray-700">Preferred Date</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="preferredDate"
                      name="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      className="pl-10"
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="priest-date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="preferredTime" className="text-gray-700">Preferred Time</Label>
                  <div className="relative mt-1">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="preferredTime"
                      name="preferredTime"
                      type="time"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      className="pl-10"
                      data-testid="priest-time-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="additionalDetails" className="text-gray-700">Additional Details</Label>
                <textarea
                  id="additionalDetails"
                  name="additionalDetails"
                  value={formData.additionalDetails}
                  onChange={handleInputChange}
                  placeholder="Any specific requirements or preferences..."
                  rows={3}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="priest-details-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !formData.serviceType}
                className="w-full bg-primary py-6 text-lg"
                data-testid="priest-submit-btn"
              >
                {loading ? (
                  <>Submitting...</>
                ) : (
                  <>
                    Book Consultation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                By submitting, you agree to our terms of service. 
                Our team will contact you within 24 hours.
              </p>
            </form>
          </div>
        </div>

        {/* Contact Banner */}
        <div className="mt-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-2">Need Help Choosing?</h2>
          <p className="text-amber-100 mb-6">
            Our religious services experts are here to guide you through the process
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href={`tel:${appConfig.support_phone_tel}`}
              className="flex items-center gap-2 px-6 py-3 bg-white text-amber-600 rounded-full font-medium hover:bg-amber-50 transition-colors"
              data-testid="priest-call-us-btn"
            >
              <Phone className="w-5 h-5" />
              Call: {appConfig.support_phone}
            </a>
            <a 
              href={appConfig.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full font-medium hover:bg-green-600 transition-colors"
              data-testid="priest-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriestServices;
